import { useMemo } from 'react';
import type { JobRequirement } from '@/raptorjob/domain/types/requirementTypes';

interface EquipmentResource {
  type?: {
    id?: number | string | null;
  } | null;
}

interface EmployeeResource {
  id: number;
}

interface RequirementEntry {
  certName: string;
  isFulfilled: boolean;
}

interface UseResourceComplianceCheckResult {
  isEquipmentCompliant: (equipment: EquipmentResource) => boolean;
  isEmployeeCompliant: (employee: EmployeeResource, roleId: number) => boolean;
  getEquipmentMissingCerts: (equipment: EquipmentResource) => string[];
  getEmployeeMissingCerts: (employee: EmployeeResource, roleId: number) => string[];
}

export function useResourceComplianceCheck(requirements: JobRequirement[]): UseResourceComplianceCheckResult {
  const equipmentRequirements = useMemo(() => {
    const map = new Map<number, RequirementEntry[]>();

    requirements.forEach((req) => {
      if (req.scopeType !== 'equipment' || !req.scopeItems) return;

      req.scopeItems.forEach((scopeItem) => {
        const key = scopeItem.typeOfEquipmentId ?? scopeItem.scopeId;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push({
          certName: req.name,
          isFulfilled: req.isFulfilled
        });
      });
    });

    return map;
  }, [requirements]);

  const crewRequirements = useMemo(() => {
    const map = new Map<number, RequirementEntry[]>();

    requirements.forEach((req) => {
      if (req.scopeType !== 'crewRoles' || !req.scopeItems) return;

      req.scopeItems.forEach((scopeItem) => {
        const key = scopeItem.roleTypeId ?? scopeItem.scopeId;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push({
          certName: req.name,
          isFulfilled: req.isFulfilled
        });
      });
    });

    return map;
  }, [requirements]);

  const isEquipmentCompliant = (equipment: EquipmentResource): boolean => {
    const typeId = equipment.type?.id;
    if (typeId == null) return true;

    const numericTypeId = typeof typeId === 'string' ? Number(typeId) : typeId;
    const reqs = equipmentRequirements.get(numericTypeId);
    if (!reqs) return true;

    return !reqs.some((r) => !r.isFulfilled);
  };

  const isEmployeeCompliant = (_employee: EmployeeResource, roleId: number): boolean => {
    const reqs = crewRequirements.get(roleId);
    if (!reqs) return true;

    return !reqs.some((r) => !r.isFulfilled);
  };

  const getEquipmentMissingCerts = (equipment: EquipmentResource): string[] => {
    const typeId = equipment.type?.id;
    if (typeId == null) return [];

    const numericTypeId = typeof typeId === 'string' ? Number(typeId) : typeId;
    const reqs = equipmentRequirements.get(numericTypeId);
    if (!reqs) return [];

    return reqs.filter((r) => !r.isFulfilled).map((r) => r.certName);
  };

  const getEmployeeMissingCerts = (_employee: EmployeeResource, roleId: number): string[] => {
    const reqs = crewRequirements.get(roleId);
    if (!reqs) return [];

    return reqs.filter((r) => !r.isFulfilled).map((r) => r.certName);
  };

  return {
    isEquipmentCompliant,
    isEmployeeCompliant,
    getEquipmentMissingCerts,
    getEmployeeMissingCerts
  };
}
